import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  flowFetch, FlowTask, FlowArea, FlowBoard, FlowUser, FlowChecklistItem,
  STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, formatDate, isOverdue, formatDuration, taskElapsed
} from './flowApi';
import { MentionField, resolveMentions } from './MentionField';
import { AttachmentList } from './FlowAttachments';

// ---------- Modal de criação de tarefa ----------
// Se fixedBoardId for informado (dentro do Kanban), o quadro/coluna são fixos.
// parentTaskId cria uma SUBTAREFA vinculada à tarefa-mãe (pode ir para outra área).
interface TaskDestination { id: string; name: string; color?: string | null; mine: boolean; boards: { id: string; name: string }[] }
interface TaskFormModalProps {
  areas: FlowArea[];
  users: FlowUser[];
  fixedBoardId?: string;
  fixedColumnId?: string;
  defaultAreaId?: string;
  parentTaskId?: string;
  parentTitle?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function TaskFormModal({ users, fixedBoardId, fixedColumnId, defaultAreaId, parentTaskId, parentTitle, onClose, onSaved }: TaskFormModalProps) {
  const [title, setTitle] = useState('');
  const [areaId, setAreaId] = useState(defaultAreaId || '');
  const [boardId, setBoardId] = useState(fixedBoardId || '');
  const [destinations, setDestinations] = useState<TaskDestination[]>([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('INVOLVED');
  const [checklist, setChecklist] = useState<string[]>([]);
  const [checkInput, setCheckInput] = useState('');
  const [isDaily, setIsDaily] = useState(false);
  const [resetTime, setResetTime] = useState('00:00');
  const [saving, setSaving] = useState(false);

  // Destinos: todas as áreas/quadros do tenant (permite delegar para outra área).
  useEffect(() => {
    if (fixedBoardId) return;
    (async () => {
      try {
        const data = await flowFetch('/api/flow/task-destinations');
        setDestinations(data.destinations || []);
      } catch { setDestinations([]); }
    })();
  }, [fixedBoardId]);

  const selectedDestination = destinations.find(d => d.id === areaId) || null;
  const boards: FlowBoard[] = (selectedDestination?.boards || []) as FlowBoard[];
  const isForeignArea = !!selectedDestination && !selectedDestination.mine;

  // Ao trocar de área, seleciona o primeiro quadro dela.
  useEffect(() => {
    if (fixedBoardId) return;
    setBoardId(selectedDestination?.boards[0]?.id || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId, destinations.length]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Informe o título da tarefa'); return; }
    if (!fixedBoardId && !boardId) { toast.error('Escolha a área e o quadro'); return; }
    setSaving(true);
    try {
      await flowFetch('/api/flow/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title, description,
          boardId: fixedBoardId || boardId,
          columnId: fixedColumnId || undefined,
          assigneeId: assigneeId || null,
          dueDate: dueDate || null, priority, visibility,
          isDaily, resetTime,
          parentTaskId: parentTaskId || undefined,
          checklist: checklist.map(t => ({ title: t }))
        })
      });
      toast.success(parentTaskId ? 'Subtarefa criada!' : 'Tarefa criada!');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao criar tarefa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="mb-1 text-lg font-bold text-slate-900">{parentTaskId ? 'Nova subtarefa' : 'Nova tarefa'}</h2>
        {parentTaskId && parentTitle && (
          <p className="mb-3 text-xs text-slate-500">Vinculada a <span className="font-semibold text-slate-700">"{parentTitle}"</span> — pode ser direcionada para qualquer área/quadro.</p>
        )}
        {!parentTaskId && <div className="mb-3" />}
        <div className="space-y-3">
          <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="O que precisa ser feito?"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500" />

          {!fixedBoardId && (
            <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500">Área</label>
                <select value={areaId} onChange={e => setAreaId(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option value="">Selecione...</option>
                  {destinations.map(a => <option key={a.id} value={a.id}>{a.name}{a.mine ? '' : ' (outra área)'}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500">Quadro</label>
                <select value={boardId} onChange={e => setBoardId(e.target.value)} disabled={!areaId}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100">
                  <option value="">{areaId ? 'Selecione...' : 'Escolha a área'}</option>
                  {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            {isForeignArea && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Você não faz parte desta área: a tarefa será visível para a equipe dela, e você continuará acompanhando por ser o criador.
              </p>
            )}
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Responsável</label>
              <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                <option value="">Sem responsável</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Prioridade</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Prazo</label>
            <input type="datetime-local" value={dueDate} onChange={e => setDueDate(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrição (opcional)" rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={visibility === 'AREA'} onChange={e => setVisibility(e.target.checked ? 'AREA' : 'INVOLVED')} className="accent-cyan-600" />
            Visível para toda a área (padrão: somente envolvidos)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={isDaily} onChange={e => setIsDaily(e.target.checked)} className="accent-violet-600" />
            Tarefa diária (reinicia todo dia)
          </label>
          {isDaily && (
            <div className="flex items-center gap-2 pl-6">
              <span className="text-xs text-slate-500">Reiniciar às</span>
              <input type="time" value={resetTime} onChange={e => setResetTime(e.target.value)}
                className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
          )}
          <div>
            <input value={checkInput} onChange={e => setCheckInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && checkInput.trim()) { setChecklist([...checklist, checkInput.trim()]); setCheckInput(''); } }}
              placeholder="Item de checklist (Enter para adicionar)"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            {checklist.length > 0 && (
              <ul className="mt-2 space-y-1">
                {checklist.map((c, i) => (
                  <li key={i} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-sm text-slate-700">
                    {c}
                    <button onClick={() => setChecklist(checklist.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50">
            {saving ? 'Criando...' : 'Criar tarefa'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Detalhe da tarefa (status = colunas do quadro) ----------
interface TaskDetailModalProps {
  taskId: string;
  areas: FlowArea[];
  users: FlowUser[];
  focusCommentId?: string; // deep-link: rola até este comentário e o destaca
  onClose: () => void;
  onChanged: () => void;
}

export function TaskDetailModal({ taskId, areas, users, focusCommentId, onClose, onChanged }: TaskDetailModalProps) {
  const [task, setTask] = useState<FlowTask | null>(null);
  const [comment, setComment] = useState('');
  const [newCheckItem, setNewCheckItem] = useState('');
  const [openTaskId, setOpenTaskId] = useState<string | null>(null); // navegar para subtarefa/mãe
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [workDraft, setWorkDraft] = useState(''); // relato "o que foi feito"
  const [savingWork, setSavingWork] = useState(false);
  const [cloning, setCloning] = useState(false);
  // Edição completa (admin): título/descrição inline + destinos para mover de área/quadro
  const [editingInfo, setEditingInfo] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [destinations, setDestinations] = useState<TaskDestination[]>([]);
  const [, setTick] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  // atualiza o cronômetro na tela enquanto estiver rodando
  useEffect(() => {
    if (!task?.timerStartedAt) return;
    const i = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(i);
  }, [task?.timerStartedAt]);

  const toggleTimer = async (running: boolean) => {
    try {
      await flowFetch(`/api/flow/tasks/${taskId}/timer/${running ? 'stop' : 'start'}`, { method: 'POST' });
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro no cronômetro');
    }
  };

  const load = async () => {
    try {
      const data = await flowFetch(`/api/flow/tasks/${taskId}`);
      setTask(data.task);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar tarefa');
      onClose();
    }
  };

  useEffect(() => { load(); }, [taskId]);

  // Sincroniza o rascunho do relato ao abrir/trocar de tarefa (sem sobrescrever digitação)
  useEffect(() => { setWorkDraft((task as any)?.workSummary || ''); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [task?.id]);

  // Destinos (todas as áreas/quadros) para o admin mover a tarefa
  const isAdminForEdit = !!user && user.role !== 'USER';
  useEffect(() => {
    if (!isAdminForEdit) return;
    (async () => {
      try {
        const data = await flowFetch('/api/flow/task-destinations');
        setDestinations(data.destinations || []);
      } catch { setDestinations([]); }
    })();
  }, [isAdminForEdit]);

  const startEditInfo = () => {
    setEditTitle(task?.title || '');
    setEditDesc(task?.description || '');
    setEditingInfo(true);
  };
  const saveInfo = async () => {
    if (!editTitle.trim()) { toast.error('Informe o título'); return; }
    await patch({ title: editTitle, description: editDesc });
    setEditingInfo(false);
  };

  // Deep-link de notificação: rola até o comentário indicado assim que a tarefa carrega
  useEffect(() => {
    if (!focusCommentId || !task) return;
    const timer = setTimeout(() => {
      document.getElementById(`flow-comment-${focusCommentId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCommentId, task?.id]);

  const saveWorkSummary = async () => {
    setSavingWork(true);
    try {
      await flowFetch(`/api/flow/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify({ workSummary: workDraft }) });
      toast.success('Relato salvo!');
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar relato');
    } finally {
      setSavingWork(false);
    }
  };

  const patch = async (body: any) => {
    try {
      await flowFetch(`/api/flow/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify(body) });
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao atualizar');
    }
  };

  const moveToColumn = async (columnId: string) => {
    try {
      await flowFetch(`/api/flow/tasks/${taskId}/move`, { method: 'PUT', body: JSON.stringify({ columnId, position: 0 }) });
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao mover');
    }
  };

  const toggleCheckItem = async (item: FlowChecklistItem) => {
    if (!item.id) return;
    try {
      await flowFetch(`/api/flow/checklist/${item.id}`, { method: 'PUT', body: JSON.stringify({ done: !item.done }) });
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao atualizar item');
    }
  };

  const addCheckItem = async () => {
    if (!newCheckItem.trim()) return;
    try {
      await flowFetch(`/api/flow/tasks/${taskId}/checklist`, { method: 'POST', body: JSON.stringify({ title: newCheckItem.trim() }) });
      setNewCheckItem('');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao adicionar item');
    }
  };

  const deleteCheckItem = async (item: FlowChecklistItem) => {
    if (!item.id) return;
    try {
      await flowFetch(`/api/flow/checklist/${item.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir item');
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      await flowFetch(`/api/flow/comments/${commentId}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir comentário');
    }
  };


  const sendComment = async () => {
    if (!comment.trim()) return;
    const mentions = resolveMentions(comment, users);
    try {
      await flowFetch('/api/flow/comments', { method: 'POST', body: JSON.stringify({ taskId, content: comment, mentions }) });
      setComment('');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao comentar');
    }
  };

  const handleDelete = async () => {
    const subtaskCount = ((task as any)?.subtasks || []).length;
    const question = subtaskCount > 0
      ? `Tem certeza que deseja excluir esta tarefa principal e suas ${subtaskCount} subtarefa(s)? Esta acao nao pode ser desfeita.`
      : 'Tem certeza que deseja excluir esta tarefa? Esta acao nao pode ser desfeita.';
    if (!confirm(question)) return;
    try {
      const data = await flowFetch(`/api/flow/tasks/${taskId}`, { method: 'DELETE' });
      toast.success(data.deletedSubtasks > 0
        ? `Tarefa e ${data.deletedSubtasks} subtarefa(s) excluídas!`
        : 'Tarefa excluída!');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const handleClone = async () => {
    if (!confirm('Clonar esta tarefa com checklist, participantes e todas as subtarefas? O progresso da copia comecara do zero.')) return;
    setCloning(true);
    try {
      const data = await flowFetch(`/api/flow/tasks/${taskId}/clone`, { method: 'POST' });
      if (data.clonedSubtasks !== data.expectedSubtasks) {
        throw new Error(`A tarefa foi copiada, mas faltaram subtarefas (${data.clonedSubtasks || 0}/${data.expectedSubtasks || 0})`);
      }
      toast.success(data.clonedSubtasks > 0
        ? `Tarefa e ${data.clonedSubtasks} subtarefa(s) clonadas!`
        : 'Tarefa clonada com sucesso!');
      onChanged();
      setOpenTaskId(data.task.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao clonar tarefa');
    } finally {
      setCloning(false);
    }
  };

  if (!task) {
    return (
      <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white"></div>
      </div>
    );
  }

  const columns = task.boardColumns || [];
  const isAdmin = !!user && user.role !== 'USER';
  const canDeleteItem = (createdById?: string | null) => isAdmin || !createdById || createdById === user?.id;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">{task.title}</h2>
              {task.isDaily && <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700">Diária</span>}
            </div>
            <p className="text-xs text-slate-500">
              {task.area?.name && <span className="font-semibold text-slate-600">{task.area.name}</span>}
              {task.board?.name && <span> · {task.board.name}</span>}
              {' · '}criada por {task.createdBy?.nome || '—'} · {formatDate(task.createdAt)}
              {isOverdue(task) && <span className="ml-2 font-bold text-red-600">ATRASADA</span>}
            </p>
            {(task as any).parent && (
              <button onClick={() => setOpenTaskId((task as any).parent.id)}
                className="mt-1 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-0.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100">
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" /></svg>
                Parte de: {(task as any).parent.title}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {task.boardId && (
              <button onClick={() => { onClose(); navigate(`/flow/boards/${task.boardId}`); }}
                className="inline-flex items-center gap-1 rounded-lg bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700 hover:bg-cyan-100">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v8a1 1 0 01-1 1h-4a1 1 0 01-1-1V5z" /></svg>
                Abrir no quadro
              </button>
            )}
            {isAdminForEdit && !editingInfo && (
              <button onClick={startEditInfo}
                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200" title="Editar título e descrição">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                Editar
              </button>
            )}
            <button onClick={handleClone} disabled={cloning}
              className="inline-flex items-center gap-1 rounded-lg bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
              title="Clonar tarefa, checklist, participantes e subtarefas">
              {cloning ? 'Clonando...' : 'Clonar'}
            </button>
            {(user && (task.createdById === user.id || user.role !== 'USER')) && (
              <button onClick={handleDelete} className="text-xs text-red-500 hover:text-red-700">Excluir</button>
            )}
            <button onClick={onClose} className="rounded-full p-1 text-slate-400 hover:bg-slate-100">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-0 overflow-y-auto md:grid-cols-5">
          <div className="space-y-4 p-6 md:col-span-3">
            {editingInfo ? (
              /* Edição completa (admin): título e descrição */
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Editar tarefa</p>
                <input value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Título"
                  className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold" />
                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={4} placeholder="Descrição"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm leading-relaxed" />
                <div className="mt-2 flex justify-end gap-2">
                  <button onClick={() => setEditingInfo(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100">Cancelar</button>
                  <button onClick={saveInfo} className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700">Salvar</button>
                </div>
              </div>
            ) : (
              task.description && <p className="whitespace-pre-wrap text-sm text-slate-700">{task.description}</p>
            )}

            {/* Handoff: o que foi feito nas etapas anteriores do fluxo (relato + anexos) */}
            {((task as any).previousStages || []).length > 0 && (
              <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-cyan-700">Etapas anteriores — o que já foi feito</p>
                <div className="space-y-2">
                  {((task as any).previousStages || []).map((p: any) => (
                    <div key={p.id} className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLORS[p.status] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[p.status] || p.status}</span>
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{p.title}</span>
                        {p.assignee?.nome && <span className="text-[11px] text-slate-400">{p.assignee.nome}</span>}
                        {p.completedAt && <span className="text-[11px] text-slate-400">· {formatDate(p.completedAt)}</span>}
                      </div>
                      {p.workSummary ? (
                        <p className="mt-2 whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">{p.workSummary}</p>
                      ) : (
                        <p className="mt-2 text-xs italic text-slate-400">Sem relato registrado nesta etapa.</p>
                      )}
                      {(p.attachments || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {p.attachments.map((a: any) => (
                            <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                              className="inline-flex max-w-[220px] items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-cyan-300 hover:bg-cyan-50">
                              <svg className="h-3.5 w-3.5 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                              <span className="truncate">{a.fileName}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Relato desta tarefa: o que a pessoa fez (visível para as próximas etapas) */}
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                O que foi feito nesta tarefa
                {(task as any).parentTaskId && <span className="ml-2 normal-case tracking-normal text-cyan-600">(as próximas etapas do fluxo verão este relato e os anexos)</span>}
              </p>
              <textarea value={workDraft} onChange={e => setWorkDraft(e.target.value)} rows={3}
                placeholder="Descreva o que você fez, decisões tomadas, o que falta, links úteis..."
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm leading-relaxed focus:ring-2 focus:ring-cyan-500" />
              {workDraft !== ((task as any).workSummary || '') && (
                <div className="mt-1 flex justify-end">
                  <button onClick={saveWorkSummary} disabled={savingWork}
                    className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700 disabled:opacity-50">
                    {savingWork ? 'Salvando...' : 'Salvar relato'}
                  </button>
                </div>
              )}
            </div>

            {(task as any).checklistFromTemplate ? (
              /* Checklist-modelo: mesmas opções da tarefa principal, mas esta etapa marca a PRÓPRIA lista */
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-500">Checklist do fluxo — sua etapa</p>
                <p className="mb-2 text-[11px] text-slate-400">As opções vêm da tarefa principal, mas cada etapa marca a sua própria lista. Itens são adicionados/removidos na tarefa principal.</p>
                <ul className="space-y-1">
                  {(task.checklist || []).map((item: any) => (
                    <li key={item.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={item.done} onChange={() => toggleCheckItem(item)} className="accent-violet-600" />
                      <span className={`flex-1 ${item.done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item.title}</span>
                    </li>
                  ))}
                  {(task.checklist || []).length === 0 && (
                    <li className="text-xs text-slate-400">A tarefa principal ainda não tem itens no checklist.</li>
                  )}
                </ul>
              </div>
            ) : (
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                  Checklist
                  {(task as any).sharedChecklist && ((task as any).subtasks || []).length > 0 && (
                    <span className="ml-2 normal-case tracking-normal text-violet-500">(modelo das subtarefas — cada etapa marca a própria cópia)</span>
                  )}
                </p>
                <ul className="space-y-1">
                  {(task.checklist || []).map(item => (
                    <li key={item.id} className="group flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={item.done} onChange={() => toggleCheckItem(item)} className="accent-cyan-600" />
                      <span className={`flex-1 ${item.done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item.title}</span>
                      {canDeleteItem(item.createdById) && (
                        <button onClick={() => deleteCheckItem(item)}
                          className="text-slate-300 opacity-0 transition hover:text-red-500 group-hover:opacity-100" title="Excluir item">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex gap-2">
                  <input value={newCheckItem} onChange={e => setNewCheckItem(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCheckItem()}
                    placeholder="Adicionar item..." className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm" />
                </div>
              </div>
            )}

            {/* Subtarefas (fluxo multi-área: cada subtarefa pode viver em outra área/quadro) */}
            <div>
              {(() => {
                const subtasks: any[] = (task as any).subtasks || [];
                const isSequential = !!(task as any).sequentialSubtasks;
                const canManageFlow = isAdmin || task.createdById === user?.id;
                // Agrupa por etapa: mesmo sequenceOrder = tarefas em paralelo (lado a lado)
                const groups: any[][] = [];
                subtasks.forEach((s: any) => {
                  const last = groups[groups.length - 1];
                  if (last && last[0].sequenceOrder === s.sequenceOrder) last.push(s);
                  else groups.push([s]);
                });
                const sendStages = async (stages: any[][]) => {
                  try {
                    await flowFetch(`/api/flow/tasks/${taskId}/subtask-order`, {
                      method: 'PUT',
                      body: JSON.stringify({ stages: stages.filter(g => g.length).map(g => g.map((t: any) => t.id)) })
                    });
                    await load();
                    onChanged();
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Erro ao reordenar');
                  }
                };
                // ↑/↓ movem a tarefa para a etapa anterior/seguinte (juntando-a às
                // tarefas de lá = paralelo). Nas pontas, cria uma etapa nova.
                const moveToStage = (gi: number, ti: number, dir: -1 | 1) => {
                  const stages = groups.map(g => [...g]);
                  const [t] = stages[gi].splice(ti, 1);
                  const target = gi + dir;
                  if (target < 0) stages.unshift([t]);
                  else if (target >= stages.length) stages.push([t]);
                  else stages[target].push(t);
                  sendStages(stages);
                };
                // Separa uma tarefa de uma etapa paralela para uma etapa própria logo após.
                const splitToOwnStage = (gi: number, ti: number) => {
                  const stages = groups.map(g => [...g]);
                  const [t] = stages[gi].splice(ti, 1);
                  stages.splice(gi + 1, 0, [t]);
                  sendStages(stages);
                };
                return (
                  <>
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Subtarefas</p>
                      <div className="flex flex-wrap items-center gap-3">
                        {subtasks.length > 0 && canManageFlow && (
                          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600" title="Cada subtarefa só aparece para o responsável quando a anterior for concluída">
                            <input type="checkbox" className="accent-violet-600" checked={isSequential}
                              onChange={e => patch({ sequentialSubtasks: e.target.checked })} />
                            Tarefas sequenciais
                          </label>
                        )}
                        {subtasks.length > 0 && canManageFlow && (
                          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600" title="Usar o checklist da tarefa principal como padrão das subtarefas: todas mostram as mesmas opções, mas cada etapa marca a sua própria lista">
                            <input type="checkbox" className="accent-violet-600" checked={!!(task as any).sharedChecklist}
                              onChange={e => patch({ sharedChecklist: e.target.checked })} />
                            Checklist padrão
                          </label>
                        )}
                        {!(task as any).parentTaskId && (
                          <button onClick={() => setShowSubtaskForm(true)}
                            className="rounded-lg bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700 hover:bg-cyan-100">
                            + Nova subtarefa
                          </button>
                        )}
                      </div>
                    </div>
                    {isSequential && subtasks.length > 0 && (
                      <p className="mb-2 rounded-lg bg-violet-50 px-3 py-1.5 text-[11px] text-violet-700">
                        Fluxo sequencial: cada etapa só aparece para o responsável quando a anterior for concluída.
                      </p>
                    )}
                    {subtasks.length > 0 && (
                      <>
                        {/* Progresso combinado (checklist + subtarefas) */}
                        <div className="mb-2 flex items-center gap-2">
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                            <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${(task as any).progress || 0}%` }} />
                          </div>
                          <span className="text-xs font-bold text-slate-600">{(task as any).progress || 0}%</span>
                        </div>
                        <ul className="space-y-2">
                          {groups.map((group, gi) => (
                            <li key={gi} className={`rounded-xl border p-2 ${group.length > 1 ? 'border-indigo-200 bg-indigo-50/40' : 'border-transparent p-0'}`}>
                              <div className="mb-1 flex items-center gap-2 px-1">
                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Etapa {gi + 1}</span>
                                {group.length > 1 && (
                                  <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-indigo-700">Em paralelo ({group.length})</span>
                                )}
                              </div>
                              <div className={`grid gap-1.5 ${group.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                                {group.map((s: any, ti: number) => (
                                  <div key={s.id} className="flex items-center gap-1.5">
                                    {canManageFlow && (
                                      <span className="flex shrink-0 flex-col items-center">
                                        <button onClick={() => moveToStage(gi, ti, -1)}
                                          className="text-slate-300 hover:text-cyan-600" title="Mover para a etapa anterior (junta em paralelo)">
                                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                                        </button>
                                        {group.length > 1 && (
                                          <button onClick={() => splitToOwnStage(gi, ti)}
                                            className="text-slate-300 hover:text-indigo-600" title="Separar em uma etapa própria">
                                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4" /></svg>
                                          </button>
                                        )}
                                        <button onClick={() => moveToStage(gi, ti, 1)}
                                          className="text-slate-300 hover:text-cyan-600" title="Mover para a próxima etapa (junta em paralelo)">
                                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                      </span>
                                    )}
                                    <button onClick={() => setOpenTaskId(s.id)} title={s.title}
                                      className={`flex min-w-0 flex-1 flex-col gap-1 overflow-hidden rounded-lg border bg-white px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${s.locked ? 'border-dashed border-slate-300 opacity-60' : 'border-slate-200'}`}>
                                      {/* Linha 1: título (trunca) + % */}
                                      <span className="flex w-full min-w-0 items-center gap-2">
                                        {s.locked && <span className="shrink-0" title="Aguardando a etapa anterior ser concluída">🔒</span>}
                                        <span className={`min-w-0 flex-1 truncate ${s.status === 'DONE' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{s.title}</span>
                                        <span className="shrink-0 text-[11px] font-bold text-slate-500">{s.progress}%</span>
                                      </span>
                                      {/* Linha 2: metadados (quebram linha se faltar espaço) */}
                                      <span className="flex w-full flex-wrap items-center gap-1.5">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLORS[s.status] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[s.status] || s.status}</span>
                                        {s.deliveredLate && (
                                          <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600" title={`Entregue ${s.lateDays} dia(s) após o prazo`}>+{s.lateDays}d atraso</span>
                                        )}
                                        {s.area?.name && (
                                          <span className="max-w-[120px] truncate rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500" style={s.area.color ? { color: s.area.color } : undefined}>{s.area.name}</span>
                                        )}
                                        {s.assignee?.nome && <span className="max-w-[100px] truncate text-[11px] text-slate-400">{s.assignee.nome.split(' ')[0]}</span>}
                                      </span>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {subtasks.length === 0 && !(task as any).parentTaskId && (
                      <p className="text-xs text-slate-400">Divida esta tarefa em etapas — cada subtarefa pode ser atribuída a outra pessoa, área ou quadro.</p>
                    )}
                  </>
                );
              })()}
            </div>

            <AttachmentList
              attachments={task.attachments || []}
              uploadPath={`/api/flow/tasks/${taskId}/attachments`}
              onChanged={load}
              currentUserId={user?.id}
              isAdmin={isAdmin}
            />

            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Comentários</p>
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {(task.comments || []).map(c => (
                  <div key={c.id} id={`flow-comment-${c.id}`}
                    className={`group rounded-lg px-3 py-2 ${c.id === focusCommentId ? 'bg-cyan-50 ring-2 ring-cyan-300' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-600">{c.author?.nome || '—'} <span className="font-normal text-slate-400">· {formatDate(c.createdAt)}</span></p>
                      {isAdmin && (
                        <button onClick={() => deleteComment(c.id)}
                          className="text-slate-300 opacity-0 transition hover:text-red-500 group-hover:opacity-100" title="Excluir comentário">
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-800">{c.content}</p>
                  </div>
                ))}
                {(task.comments || []).length === 0 && <p className="text-sm text-slate-400">Nenhum comentário ainda.</p>}
              </div>
              <div className="mt-2 flex gap-2">
                <MentionField value={comment} onChange={setComment} users={users} onSubmit={sendComment}
                  placeholder="Comentar... digite @ para mencionar" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button onClick={sendComment} className="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700">Enviar</button>
              </div>
            </div>
          </div>

          <div className="space-y-3 border-t border-slate-200 bg-slate-50 p-6 md:col-span-2 md:border-l md:border-t-0">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Status (coluna do quadro)</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {columns.map(col => (
                  <button key={col.id} onClick={() => moveToColumn(col.id)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${task.columnId === col.id ? (STATUS_COLORS[col.kind] || 'bg-cyan-100 text-cyan-700') + ' ring-2 ring-cyan-400' : 'bg-white text-slate-500 hover:bg-slate-100'}`}>
                    {col.name}
                  </button>
                ))}
                {columns.length === 0 && <span className="text-xs text-slate-400">Sem quadro associado</span>}
              </div>
            </div>

            {/* Cronômetro */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Tempo</p>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-lg font-bold text-slate-800 tabular-nums">{formatDuration(taskElapsed(task))}</span>
                {task.timerStartedAt ? (
                  <button onClick={() => toggleTimer(true)} className="rounded-lg bg-amber-500 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-600">⏸ Pausar</button>
                ) : (
                  <button onClick={() => toggleTimer(false)} className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">▶ Start</button>
                )}
                {task.timerStartedAt && <span className="text-[10px] font-semibold text-emerald-600">rodando…</span>}
              </div>
              {task.status === 'DONE' && <p className="mt-0.5 text-[11px] text-slate-400">Tempo até concluir esta tarefa.</p>}
            </div>

            {/* Tarefa diária */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Tarefa diária</p>
              <label className="mt-1 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="accent-violet-600" checked={!!task.isDaily}
                  onChange={e => patch({ isDaily: e.target.checked, resetTime: task.resetTime || '00:00' })} />
                Reinicia todo dia
              </label>
              {task.isDaily && (
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Reiniciar às</span>
                  <input type="time" value={task.resetTime || '00:00'} onChange={e => patch({ resetTime: e.target.value })}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Prioridade</p>
              <select value={task.priority} onChange={e => patch({ priority: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Responsável</p>
              <select value={task.assigneeId || ''} onChange={e => patch({ assigneeId: e.target.value || null })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                <option value="">Sem responsável</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Prazo</p>
              <input type="datetime-local" value={task.dueDate ? task.dueDate.slice(0, 16) : ''}
                onChange={e => patch({ dueDate: e.target.value || null })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            {/* Edição completa (admin): visibilidade e mover de área/quadro */}
            {isAdminForEdit && (
              <>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Visibilidade</p>
                  <select value={task.visibility || 'INVOLVED'} onChange={e => patch({ visibility: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                    <option value="INVOLVED">Somente envolvidos</option>
                    <option value="AREA">Visível para toda a área</option>
                  </select>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Área / Quadro</p>
                  <select value={task.boardId || ''}
                    onChange={e => { if (e.target.value && e.target.value !== task.boardId) patch({ boardId: e.target.value }); }}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                    <option value="">Sem quadro</option>
                    {destinations.map(d => (
                      <optgroup key={d.id} label={d.name}>
                        {d.boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <p className="mt-0.5 text-[10px] text-slate-400">Mover de quadro leva a tarefa para a primeira coluna do destino.</p>
                </div>
              </>
            )}

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Participantes</p>
              {isAdminForEdit ? (
                <details className="mt-1 text-sm text-slate-600">
                  <summary className="cursor-pointer select-none">
                    {(task.participants || []).map(p => p.user?.nome).filter(Boolean).join(', ') || 'Nenhum'} <span className="text-[10px] text-cyan-600">(editar)</span>
                  </summary>
                  <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
                    {users.map(u => {
                      const ids = (task.participants || []).map((p: any) => p.userId);
                      const checked = ids.includes(u.id);
                      return (
                        <label key={u.id} className="flex items-center gap-2 text-xs text-slate-700">
                          <input type="checkbox" className="accent-cyan-600" checked={checked}
                            onChange={e => patch({ participantIds: e.target.checked ? [...ids, u.id] : ids.filter((id: string) => id !== u.id) })} />
                          {u.nome}
                        </label>
                      );
                    })}
                  </div>
                </details>
              ) : (
                <p className="mt-1 text-sm text-slate-600">
                  {(task.participants || []).map(p => p.user?.nome).filter(Boolean).join(', ') || 'Nenhum'}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Atividades</p>
              <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto text-xs text-slate-500">
                {(task.activities || []).map(a => (
                  <li key={a.id}><span className="font-semibold">{a.user?.nome || '—'}</span> {a.message} · {formatDate(a.createdAt)}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Navegação para subtarefa/tarefa-mãe (modal sobreposto) */}
      {openTaskId && (
        <TaskDetailModal taskId={openTaskId} areas={areas} users={users}
          onClose={() => setOpenTaskId(null)} onChanged={() => { load(); onChanged(); }} />
      )}
      {/* Criar subtarefa (pode ir para qualquer área/quadro) */}
      {showSubtaskForm && (
        <TaskFormModal areas={areas} users={users} parentTaskId={task.id} parentTitle={task.title}
          defaultAreaId={task.areaId || undefined}
          onClose={() => setShowSubtaskForm(false)} onSaved={() => { load(); onChanged(); }} />
      )}
    </div>
  );
}

// ---------- Lista pessoal de tarefas (Minhas / Delegadas) ----------
const QUICK_FILTERS: { id: string; label: string }[] = [
  { id: 'all', label: 'Todas' },
  { id: 'today', label: 'Hoje' },
  { id: 'overdue', label: 'Atrasadas' },
  { id: 'TODO', label: 'A fazer' },
  { id: 'DOING', label: 'Fazendo' },
  { id: 'WAITING', label: 'Aguardando' },
  { id: 'DONE', label: 'Concluídas' },
  { id: 'delegated', label: 'Delegadas por mim' },
];

interface FlowTaskListProps {
  areas: FlowArea[];
  users: FlowUser[];
  fixedFilter?: string; // 'mine' | 'delegated'
  fixedAreaId?: string; // trava a lista numa área (aba Tarefas da área)
  title?: string;
}

export function FlowTaskList({ areas, users, fixedFilter, fixedAreaId, title }: FlowTaskListProps) {
  const [tasks, setTasks] = useState<FlowTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [quick, setQuick] = useState('all');
  const [areaFilter, setAreaFilter] = useState(fixedAreaId || '');
  const [boardFilter, setBoardFilter] = useState('');
  const [boards, setBoards] = useState<FlowBoard[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [focusCommentId, setFocusCommentId] = useState<string | null>(null);
  const location = useLocation();

  // Deep-link de notificação: ?t=<taskId>&c=<commentId> abre a tarefa (e rola até o comentário)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('t');
    if (t) {
      setDetailId(t);
      setFocusCommentId(params.get('c'));
      // limpa a query para um refresh não reabrir
      try { window.history.replaceState({}, document.title, location.pathname); } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Visão pessoal "Minhas Tarefas": mais enxuta (cartões por status, sem filtros pesados)
  const simple = fixedFilter === 'mine';
  const effectiveAreaId = fixedAreaId || areaFilter;

  // quadros da área escolhida no filtro
  useEffect(() => {
    if (!effectiveAreaId) { setBoards([]); setBoardFilter(''); return; }
    (async () => {
      try {
        const data = await flowFetch(`/api/flow/boards?areaId=${effectiveAreaId}`);
        setBoards(data.boards);
      } catch { setBoards([]); }
    })();
  }, [effectiveAreaId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fixedFilter) params.set('filter', fixedFilter);
      // "Delegadas por mim" e Hoje/Atrasadas substituem o filtro pessoal
      if (['today', 'overdue', 'delegated'].includes(quick)) params.set('filter', quick);
      if (['TODO', 'DOING', 'WAITING', 'DONE'].includes(quick)) params.set('status', quick);
      if (effectiveAreaId) params.set('areaId', effectiveAreaId);
      if (boardFilter) params.set('boardId', boardFilter);
      if (assigneeFilter) params.set('assigneeId', assigneeFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      if (search) params.set('search', search);
      const data = await flowFetch(`/api/flow/tasks?${params}`);
      setTasks(data.tasks);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [quick, effectiveAreaId, boardFilter, assigneeFilter, priorityFilter, search]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {title && <h2 className="mr-auto text-lg font-bold text-slate-900">{title}</h2>}
        {!title && <span className="mr-auto" />}
        <button onClick={() => setShowCreate(true)}
          className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cyan-700">
          + Nova tarefa
        </button>
      </div>

      {fixedFilter !== 'delegated' && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {QUICK_FILTERS.map(f => (
            <button key={f.id} onClick={() => setQuick(f.id)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${quick === f.id ? 'bg-cyan-600 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar tarefas..."
          className="w-56 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        {/* Filtros avançados só nas visões não-pessoais (delegadas, admin, área) */}
        {!simple && !fixedAreaId && (
          <select value={areaFilter} onChange={e => setAreaFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
            <option value="">Por área</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        {!simple && (
          <>
            <select value={boardFilter} onChange={e => setBoardFilter(e.target.value)} disabled={!effectiveAreaId}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100">
              <option value="">Por quadro</option>
              {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
              <option value="">Por responsável</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
              <option value="">Por prioridade</option>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>
      ) : tasks.length === 0 ? (
        <p className="py-12 text-center text-slate-400">Nenhuma tarefa por aqui. Clique em “+ Nova tarefa” para começar.</p>
      ) : simple ? (
        // Visão pessoal: cartões agrupados por status
        <div className="space-y-5">
          {['TODO', 'DOING', 'WAITING', 'DONE'].map(st => {
            const group = tasks.filter(t => t.status === st);
            if (group.length === 0) return null;
            return (
              <div key={st}>
                <div className="mb-2 flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[st]}`}>{STATUS_LABELS[st]}</span>
                  <span className="text-xs font-semibold text-slate-400">{group.length}</span>
                </div>
                <div className="space-y-2">
                  {group.map(t => (
                    <button key={t.id} onClick={() => setDetailId(t.id)}
                      className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition hover:border-cyan-300 hover:shadow-md">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${PRIORITY_COLORS[t.priority].split(' ')[0]}`} title={PRIORITY_LABELS[t.priority]} />
                      <div className="min-w-0 flex-1">
                        {(t as any).parent && (
                          <p className="flex items-center gap-1 truncate text-[11px] font-semibold text-violet-600" title={`Subtarefa de: ${(t as any).parent.title}`}>
                            <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" /></svg>
                            <span className="truncate">{(t as any).parent.title}</span>
                          </p>
                        )}
                        <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-slate-800">
                          {t.isDaily && <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-violet-700">Diária</span>}
                          <span className="truncate">{t.title}</span>
                        </p>
                        <p className="truncate text-xs text-slate-400">
                          {t.area?.name || 'Sem área'}{t.board?.name ? ` · ${t.board.name}` : ''}
                        </p>
                      </div>
                      {t.dueDate && (
                        <span className={`shrink-0 text-xs font-semibold ${isOverdue(t) ? 'text-red-600' : 'text-slate-400'}`}>
                          {formatDate(t.dueDate)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Tarefa</th>
                <th className="px-4 py-3">Responsável</th>
                <th className="px-4 py-3">Área</th>
                <th className="px-4 py-3">Quadro</th>
                <th className="px-4 py-3">Prazo</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Prioridade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tasks.map(t => (
                <tr key={t.id} onClick={() => setDetailId(t.id)} className="cursor-pointer hover:bg-cyan-50/50">
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {(t as any).parent && (
                      <span className="block truncate text-[11px] font-semibold text-violet-600" title={`Subtarefa de: ${(t as any).parent.title}`}>↩ {(t as any).parent.title}</span>
                    )}
                    {t.isDaily && <span className="mr-1.5 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-violet-700">Diária</span>}
                    {t.title}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{t.assignee?.nome || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{t.area?.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{t.board?.name || '—'}</td>
                  <td className={`px-4 py-3 ${isOverdue(t) ? 'font-bold text-red-600' : 'text-slate-600'}`}>{formatDate(t.dueDate)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span></td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY_COLORS[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <TaskFormModal areas={areas} users={users} defaultAreaId={areaFilter || undefined} onClose={() => setShowCreate(false)} onSaved={load} />
      )}
      {detailId && (
        <TaskDetailModal taskId={detailId} areas={areas} users={users} focusCommentId={focusCommentId || undefined}
          onClose={() => { setDetailId(null); setFocusCommentId(null); }} onChanged={load} />
      )}
    </div>
  );
}
